import React from 'react';
import { 
  Gavel, 
  Users, 
  Lock, 
  CheckCircle2, 
  ArrowRight, 
  ShieldCheck, 
  BarChart3 
} from 'lucide-react';

export default function Governance() {
  const pillars = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Community Greenlighting",
      desc: "No single executive decides which films get made. The Mbogi community votes on project proposals based on creative merit and financial viability."
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Locked Execution",
      desc: "Once a vote passes, the terms are locked into a Smart Contract. The budget, profit splits, and deadlines cannot be changed by anyone."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Transparent Audits",
      desc: "Every cent of production spend is recorded on-chain. Investors and creators can track the budget in real-time as the film moves from script to screen."
    }
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-slate-900">
      
      {/* --- HERO SECTION --- */}
      <section className="pt-24 pb-16 px-6 bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-[#c07d3e] px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
            <Gavel className="w-3 h-3" /> The Mbogi Constitution
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight text-[#0a1d2e]">
            Decentralized <span className="text-[#c07d3e]">Decision Making</span>
          </h1>
          <p className="text-slate-500 text-lg md:text-xl leading-relaxed">
            MbogiCapital isn't owned by a board of directors. It is governed by the creators, 
            investors, and fans who power the ecosystem.
          </p>
        </div>
      </section>

      {/* --- VOTING VISUALIZER (USER FRIENDLY) --- */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className="lg:w-1/2">
            <h2 className="text-3xl font-extrabold mb-6 text-[#0a1d2e]">How Voting Works</h2>
            <p className="text-slate-500 mb-8">
              Your influence in the ecosystem is tied to your contribution. Whether you've invested capital or earned "Skill Equity" through work, you have a seat at the table.
            </p>
            
            <div className="space-y-6">
              {pillars.map((pillar, i) => (
                <div key={i} className="flex gap-5 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center text-[#c07d3e] group-hover:bg-[#c07d3e] group-hover:text-white transition-all">
                    {pillar.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0a1d2e] mb-1">{pillar.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:w-1/2 w-full">
            {/* Mock Proposal Card */}
            <div className="bg-[#0a1d2e] rounded-[3rem] p-8 md:p-12 shadow-2xl relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-8">
                  <span className="bg-orange-500/20 text-orange-400 text-[10px] font-bold px-3 py-1 rounded-full border border-orange-500/30 uppercase tracking-widest">
                    Active Proposal #104
                  </span>
                  <div className="text-white/40 text-[10px] font-mono">Ends in: 14h 22m</div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">"Nairobi Nights" Greenlight</h3>
                <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                  Budget: $150,000 | Genre: Sci-Fi Noir | Crew: 45 Members
                </p>

                {/* Voting Bars */}
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between text-xs font-bold text-white mb-2 uppercase tracking-tighter">
                      <span>Yes (Approve Project)</span>
                      <span className="text-green-400">88%</span>
                    </div>
                    <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '88%' }}></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-xs font-bold text-white mb-2 uppercase tracking-tighter">
                      <span>No (Reject Proposal)</span>
                      <span className="text-red-400">12%</span>
                    </div>
                    <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: '12%' }}></div>
                    </div>
                  </div>
                </div>

                <button className="w-full mt-10 py-4 bg-white hover:bg-slate-100 text-[#0a1d2e] rounded-2xl font-bold flex items-center justify-center gap-2 transition-all">
                  Cast Your Vote <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Decorative Background Flare */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#c07d3e] opacity-10 blur-[100px] -mr-32 -mt-32"></div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SECURITY / TRUST SECTION --- */}
      <section className="py-20 px-6 bg-slate-900 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
          <ShieldCheck className="w-16 h-16 text-[#c07d3e] mb-8" />
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6">Unstoppable Integrity</h2>
          <p className="max-w-2xl text-slate-400 mb-12 leading-relaxed">
            The Mbogi Protocol is built on the Ethereum blockchain. This means even the 
            founders cannot touch the funds once a project has been greenlit by the community. 
            The code is the law.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full border-t border-slate-800 pt-16">
            {[
              { label: "Proposals Passed", val: "142" },
              { label: "Active Voters", val: "12,400+" },
              { label: "Community Treasury", val: "$4.2M" },
              { label: "Success Rate", val: "94%" }
            ].map((stat, i) => (
              <div key={i}>
                <p className="text-3xl font-black text-white mb-1">{stat.val}</p>
                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FINAL CTA --- */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto bg-slate-50 p-12 md:p-20 rounded-[4rem] border border-slate-100 shadow-sm">
          <h2 className="text-3xl font-extrabold mb-6 text-[#0a1d2e]">Take Your Seat at the Table</h2>
          <p className="text-slate-500 mb-10 leading-relaxed">
            Ready to participate in the future of African cinema? Join the waitlist and 
            get your voting power ready for the next drop.
          </p>
          <button className="px-12 py-5 bg-[#c07d3e] text-white rounded-2xl font-black text-lg shadow-xl shadow-orange-100 hover:scale-105 transition-all">
            Join the Mbogi
          </button>
        </div>
      </section>
    </div>
  );
}